import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '../../design-system/primitives/Button/Button';
import styles from './ShellMenu.module.css';

/**
 * The topbar's switch: an icon button that opens a short list of mutually
 * exclusive values.
 *
 * **`menuitemradio`, not `menuitem`.** These menus do not run commands, they
 * choose a value out of a set — and a plain `menuitem` announces nothing about
 * which value is live, so a screen-reader user opening the language menu has no
 * way to learn what language they are already in. `aria-checked` is the whole
 * point of the control. (The plan's sample test queries
 * `getByRole('menuitem', …)`, which does not match `menuitemradio`; see the
 * report's plan-defect list.)
 *
 * **It does not portal.** `overlayRoot.ts` is the single portal host and it is
 * deliberately never hidden from assistive technology, which is exactly why a
 * shell menu must not live there: an appearance switch behind an open modal
 * would stay announceable and, worse, reachable. This popover is an ordinary
 * absolutely-positioned child of the trigger's wrapper, so a dialog's
 * background `aria-hidden` covers it like everything else on the page.
 *
 * **Escape stops here.** Task 21 §6.2 and task 22 §7.2: an Escape aimed at this
 * menu must not also close the dialog the shell is sitting under.
 */

export interface ShellMenuOption<T extends string> {
  value: T;
  label: string;
  /**
   * A visual beside (list) or above (thumbnails) the label — the accent
   * picker's colour swatch, the background picker's thumbnail. Decorative:
   * the label stays the item's accessible name.
   */
  visual?: ReactNode;
}

export interface ShellMenuProps<T extends string> {
  /** The switch's accessible name — "Language", "Theme", "Row density". */
  label: string;
  /** Decorative; the name comes from `label`. */
  icon: ReactNode;
  /**
   * The value that is live, for a menu that CHOOSES one.
   *
   * Omit it, and every item becomes a `menuitem` that RUNS something instead of
   * a `menuitemradio` that reports a state. That distinction is the reason this
   * prop is optional rather than the account menu getting its own copy of the
   * two hundred lines of roving-focus and dismissal logic below: the keyboard
   * contract for the two kinds of menu is identical, and only the role, the
   * `aria-checked` and the index the menu opens on differ.
   *
   * `aria-checked` on a command is a lie a screen reader repeats — "Sign out,
   * not checked" — so it is absent rather than false.
   */
  value?: T;
  options: readonly ShellMenuOption<T>[];
  onSelect: (value: T) => void;
  testId?: string;
  /**
   * `thumbnails` lays the items out as a grid of previews (the background
   * picker). Left/Right then move along the grid as well as Up/Down.
   */
  layout?: 'list' | 'thumbnails';
}

export function ShellMenu<T extends string>({
  label,
  icon,
  value,
  options,
  onSelect,
  testId,
  layout = 'list',
}: ShellMenuProps<T>) {
  /**
   * A menu that was given a `value` reports a choice; one that was not runs
   * commands. Derived once here rather than re-tested at each of the four
   * places it matters, so the two kinds cannot drift apart.
   */
  const selectable = value !== undefined;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Focus follows the active index while the menu is open. A menu whose items
  // are only *styled* as active leaves the screen reader on the trigger, so it
  // reads nothing as the arrows move.
  useEffect(() => {
    if (!open) return;
    itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  function openAt(index: number) {
    setActiveIndex(index);
    setOpen(true);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      // Opening onto the CHECKED value, not onto the first one: the list is a
      // set of values, so the place to start is where you already are. A
      // command menu has no "where you already are", so it opens on the first
      // item — which `findIndex` returning -1 and `Math.max(0, …)` already
      // produce, but written out because relying on that was accidental.
      event.preventDefault();
      openAt(selectable ? Math.max(0, options.findIndex((option) => option.value === value)) : 0);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(options.length - 1);
    }
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + options.length) % options.length);
        break;
      case 'ArrowRight':
      case 'ArrowLeft': {
        if (layout !== 'thumbnails') break;
        event.preventDefault();
        // The grid flows with the writing direction, so "forward" is Right in
        // English and Left in Arabic.
        const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
        const forward = (event.key === 'ArrowRight') !== rtl;
        setActiveIndex((index) => (index + (forward ? 1 : -1) + options.length) % options.length);
        break;
      }
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case 'Escape':
        // Not `preventDefault`: nothing native happens. `stopPropagation` is the
        // load-bearing half — see the module comment.
        event.stopPropagation();
        close(true);
        break;
      // Tab is deliberately NOT handled here. This is a menu, not a modal, so
      // Tab must leave — and the `onBlur` below already closes it once focus has
      // gone. An explicit `case 'Tab': close(false)` was tried and removed for
      // two reasons: no test could tell the two apart (the blur path fires
      // either way), and closing DURING the keydown unmounts the menu before
      // the browser performs the default focus move, so the next Tab stop is
      // computed from a node that no longer exists.
      default:
        break;
    }
  }

  return (
    <div
      className={styles['menu']}
      ref={wrapperRef}
      onBlur={(event) => {
        // A click or a Tab that lands outside closes the menu. `relatedTarget`
        // is null when focus leaves the document entirely, which must NOT close
        // it — switching windows and coming back should find the menu as it was.
        const next = event.relatedTarget;
        if (next instanceof Node && wrapperRef.current?.contains(next) === true) return;
        if (next === null) return;
        setOpen(false);
      }}
    >
      <Button
        variant="icon"
        ref={triggerRef}
        icon={icon}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={testId}
        onClick={() =>
          open
            ? close(false)
            : openAt(
                selectable ? Math.max(0, options.findIndex((option) => option.value === value)) : 0,
              )
        }
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={styles['srOnly']}>{label}</span>
      </Button>

      {open ? (
        <div
          role="menu"
          id={menuId}
          aria-label={label}
          className={[styles['list'], layout === 'thumbnails' ? styles['thumbnails'] : null]
            .filter(Boolean)
            .join(' ')}
          // APG: the menu container is programmatically focusable so a script
          // (or a screen reader's own navigation) can land on it without it
          // becoming a Tab stop of its own. It is also what
          // `jsx-a11y/interactive-supports-focus` is asking for, correctly.
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
        >
          {options.map((option, index) => (
            <button
              type="button"
              key={option.value}
              role={selectable ? 'menuitemradio' : 'menuitem'}
              aria-checked={selectable ? option.value === value : undefined}
              className={styles['option']}
              ref={(node) => {
                itemsRef.current[index] = node;
              }}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => {
                onSelect(option.value);
                close(true);
              }}
            >
              {option.visual === undefined ? null : (
                <span className={styles['visual']} aria-hidden="true">
                  {option.visual}
                </span>
              )}
              <span className={styles['optionLabel']}>{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
